// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Button, ButtonToggle, Flex, useToast } from '@iheartjane/reefer';
import type {
  ColumnFiltersState,
  RowSelectionState,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { ButtonToggleStylesOverrideWrapper } from '@jane/business-admin/components/shared/tables/ButtonToggleStylesOverrideWrapper';
import {
  useArchiveUnpublishedProduct,
  useAutoPublishSettings,
  useBulkPublishProducts,
  useStoreSettings,
  useUpdateBulkProducts,
} from '@jane/business-admin/data-access';
import type { BulkPublishProductParams } from '@jane/business-admin/data-access';
import {
  useModalActionsWithTracking,
  useNavigateAndTrack,
  useStoreId,
} from '@jane/business-admin/hooks';
import { useProductsTable } from '@jane/business-admin/providers/ProductsTableProvider';
import {
  isProductTableType,
  ProductTableType,
} from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  NavigationSourceIds,
  businessPaths,
  track,
} from '@jane/business-admin/util';

import { AutoPublishModal } from '../modal/AutoPublishModal';
import { ClearModal } from '../modal/ClearModal';
import { RevertModal } from '../modal/RevertModal';
import { BulkEditModal } from './BulkEditModal';
import { HiddenProductTable } from './HiddenProductTable';
import { PublishedProductTable } from './PublishedProductTable';
import { UnpublishedProductTable } from './UnpublishedProductTable';

const AutoPublishButtonWrapper = styled.div({
  minWidth: '181px',
});

const BulkEditButtonWrapper = styled.div({
  minWidth: '99px',
});

const ClearPotenciesButtonWrapper = styled.div({
  minWidth: '99px',
});

const HeaderButton = ({
  onClick,
  selectedProducts,
  text,
  productTableType,
  publishDisabled = false,
}: {
  onClick: any;
  productTableType: ProductTableType;
  publishDisabled?: boolean;
  selectedProducts: RowSelectionState;
  text: string;
}) => {
  const showRevertButton =
    useFlag(FLAGS.cnBulkRevertProductImagesDescriptions) &&
    (productTableType === ProductTableType.published ||
      productTableType === ProductTableType.hidden);
  const selectedLength = Object.keys(selectedProducts).length;
  const hasSelected = selectedLength !== 0;
  const label = (buttonText: string) =>
    hasSelected ? `${buttonText} (${selectedLength})` : buttonText;

  return (
    <>
      {text === 'Publish' ? (
        <Button
          mr={16}
          label={label('Archive')}
          variant="secondary"
          disabled={!hasSelected}
          onClick={() => onClick('Archive')}
        />
      ) : null}
      {showRevertButton ? (
        <Button
          mr={16}
          label={label('Revert')}
          variant="secondary"
          disabled={!hasSelected}
          onClick={() => onClick('Revert')}
        />
      ) : null}
      <Button
        label={label(text)}
        variant="secondary"
        disabled={!hasSelected || publishDisabled}
        onClick={onClick}
      />
    </>
  );
};

const buttonTextMapping: Record<ProductTableType, string> = {
  [ProductTableType.published]: 'Hide',
  [ProductTableType.hidden]: 'Make visible',
  [ProductTableType.unpublished]: 'Publish',
} as const;

// Products captured for bulk actions. Mirrors the publish payload, with
// `image_urls`/`status` added (we read `status` to detect invalid ones) and
// potencies optional since invalid products may lack lab_results.
type SelectedProduct = Omit<
  BulkPublishProductParams,
  | 'brand_subtype'
  | 'dosage'
  | 'lineage'
  | 'percent_cbd'
  | 'percent_thc'
  | 'size'
  | 'subcategory'
> & {
  image_urls: string[];
  percent_cbd?: number;
  percent_thc?: number;
  status?: string;
};

export const ProductTable = () => {
  const storeId = useStoreId();
  const { canEditProducts } = useProductsTable();
  const navigate = useNavigateAndTrack();

  const { type = 'published' } = useParams<{
    type: keyof typeof ProductTableType;
  }>();
  const { product_id = '' } = useParams<'product_id'>();
  const [selected, setSelected] = useState<ProductTableType>(
    ProductTableType[type] || ProductTableType.published
  );
  const [selectedProducts, setSelectedProducts] = useState<RowSelectionState>(
    {}
  );
  const [fullSelectedProducts, setFullSelectedProducts] = useState<{
    products: SelectedProduct[];
  }>({ products: [] });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const showClearButton = useFlag(FLAGS.cnBulkClearProductPotencies);
  const bulkEditInvalidProducts = useFlag(FLAGS.c1BulkEditInvalidProducts);

  // Invalid products can be archived but not published, so disable Publish and
  // show a banner when any is selected. Archive stays available for all.
  const hasInvalidSelected = useMemo(() => {
    if (!bulkEditInvalidProducts || selected !== ProductTableType.unpublished) {
      return false;
    }

    return fullSelectedProducts.products.some(
      (product) => product.status === 'invalid_data'
    );
  }, [bulkEditInvalidProducts, selected, fullSelectedProducts]);

  const hasArchivedFilter = useMemo(() => {
    return columnFilters.some((filter) => {
      return (
        filter.id === 'status' &&
        (filter.value as string[]).includes('archived')
      );
    });
  }, [JSON.stringify(columnFilters)]);

  // When tab is selected, update URL
  useEffect(() => {
    navigate(
      `${businessPaths.storeProducts(storeId, selected.toLowerCase())}${
        product_id ? '/' + product_id : ''
      }`,
      NavigationSourceIds.TableToggle,
      storeId
    );
  }, [selected, storeId]);

  const toast = useToast();
  const { data: storePayload } = useStoreSettings(storeId);
  const { data: autoPublishSettings } = useAutoPublishSettings(storeId);
  const {
    modalOpen: autoPublishModalOpen,
    openModal: setAutoPublishModalOpen,
    closeModal: setAutoPublishModalClose,
  } = useModalActionsWithTracking(ModalNames.ProductAutoPublish);
  const {
    modalOpen: bulkEditModalOpen,
    openModal: setBulkEditModalOpen,
    closeModal: setBulkEditModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    modalOpen: revertModalOpen,
    openModal: setRevertModalOpen,
    closeModal: setRevertModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    modalOpen: clearModalOpen,
    openModal: setClearModalOpen,
    closeModal: setClearModalClose,
  } = useModalActionsWithTracking(ModalNames.BulkEditProducts);
  const {
    mutateAsync: bulkUpdateProducts,
    isPending: bulkUpdateProductsLoading,
    isSuccess: bulkUpdateProductsSuccess,
    isError: bulkUpdateProductsError,
  } = useUpdateBulkProducts(storeId);
  const {
    mutateAsync: bulkPublishProducts,
    isPending: bulkPublishProductsLoading,
    isSuccess: bulkPublishProductsSuccess,
    isError: bulkPublishProductsError,
  } = useBulkPublishProducts(storeId);
  const {
    mutateAsync: bulkArchiveProducts,
    isPending: bulkArchiveProductsLoading,
    isSuccess: bulkArchiveProductsSuccess,
    isError: bulkArchiveProductsError,
  } = useArchiveUnpublishedProduct(storeId);

  const trackSubmit = (action: string) => {
    track({
      event: EventNames.BulkEdited,
      objects: Object.keys(selectedProducts),
      action: action,
    });
  };

  const table = (isBulkEditTable = false) =>
    selected === ProductTableType.published ? (
      <PublishedProductTable
        isBulkEditTable={isBulkEditTable}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : selected === ProductTableType.hidden ? (
      <HiddenProductTable
        isBulkEditTable={isBulkEditTable}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : selected === ProductTableType.unpublished ? (
      <UnpublishedProductTable
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        isBulkEditTable={isBulkEditTable}
        setFullSelectedProducts={setFullSelectedProducts}
        setColumnFiltersParent={setColumnFilters}
        columnFiltersParent={columnFilters}
        bulkEditModalOpen={bulkEditModalOpen}
      />
    ) : null;

  const buttonText = buttonTextMapping[selected];

  const onHeaderButtonClick = (action?: string) => {
    const selections = Object.keys(selectedProducts);

    if (action === 'Revert') {
      setRevertModalOpen();
    } else if (selected === ProductTableType.published) {
      bulkUpdateProducts({
        data: { visible: false },
        deletePhotos: false,
        productIds: selections,
      });
      trackSubmit('hide');
      setBulkEditModalClose();
      setSelectedProducts({});
    } else if (selected === ProductTableType.hidden) {
      bulkUpdateProducts({
        data: { visible: true },
        deletePhotos: false,
        productIds: selections,
      });
      trackSubmit('make visible');
      setBulkEditModalClose();
      setSelectedProducts({});
    } else if (selected === ProductTableType.unpublished) {
      if (action === 'Archive') {
        bulkArchiveProducts(selections);
        trackSubmit('archive');
      } else {
        // Cast: the mutation's param type predates this capture shape but
        // spreads it into the expected `{ products: [...] }` payload.
        bulkPublishProducts(
          fullSelectedProducts as unknown as Parameters<
            typeof bulkPublishProducts
          >[0]
        );
        trackSubmit('publish');
      }
      setBulkEditModalClose();
      setSelectedProducts({});
    }
  };

  useEffect(() => {
    // hide & make visible
    if (bulkUpdateProductsSuccess) {
      toast.add({
        label: 'Bulk update succeeded!',
        variant: 'success',
      });
    }
    if (bulkUpdateProductsError) {
      toast.add({
        label: 'Bulk update failed!',
        variant: 'error',
      });
    }
    if (bulkUpdateProductsLoading) {
      toast.add({
        label: 'Updating products...',
        variant: 'success',
      });
    }

    // publish
    if (bulkPublishProductsSuccess) {
      toast.add({
        label: 'Bulk publish succeeded!',
        variant: 'success',
      });
    }
    if (bulkPublishProductsError) {
      toast.add({
        label: 'Bulk publish failed!',
        variant: 'error',
      });
    }
    if (bulkPublishProductsLoading) {
      toast.add({
        label: 'Publishing...',
        variant: 'success',
      });
    }

    // archive
    if (bulkArchiveProductsSuccess) {
      toast.add({
        label: 'Bulk archive succeeded!',
        variant: 'success',
      });
    }
    if (bulkArchiveProductsError) {
      toast.add({
        label: 'Bulk archive failed!',
        variant: 'error',
      });
    }
    if (bulkArchiveProductsLoading) {
      toast.add({
        label: 'Archiving...',
        variant: 'success',
      });
    }
  }, [
    bulkUpdateProductsSuccess,
    bulkUpdateProductsError,
    bulkUpdateProductsLoading,
    bulkPublishProductsLoading,
    bulkPublishProductsSuccess,
    bulkPublishProductsError,
    bulkArchiveProductsLoading,
    bulkArchiveProductsSuccess,
    bulkArchiveProductsError,
  ]);

  return (
    <>
      <Box width="100%" height="60%" ml={-64} position="absolute">
        <Flex justifyContent="space-between" mb={24} mx={64}>
          <Flex gap={16} alignItems="center">
            <ButtonToggleStylesOverrideWrapper>
              <ButtonToggle
                value={selected}
                onChange={(value) => {
                  if (isProductTableType(value)) {
                    setSelected(value);
                  }
                  setSelectedProducts({});
                }}
                full={false}
              >
                <ButtonToggle.Button
                  value={ProductTableType.published}
                  label="Published"
                />
                <ButtonToggle.Button
                  value={ProductTableType.hidden}
                  label="Hidden"
                />
                <ButtonToggle.Button
                  value={ProductTableType.unpublished}
                  label="Unpublished"
                />
              </ButtonToggle>
            </ButtonToggleStylesOverrideWrapper>
          </Flex>
          <Flex gap={16}>
            {showClearButton ? (
              <ClearPotenciesButtonWrapper>
                <Button
                  key="clear-potencies"
                  label="Clear potencies"
                  variant="secondary"
                  onClick={() => setClearModalOpen()}
                  disabled={!canEditProducts || hasArchivedFilter}
                />
              </ClearPotenciesButtonWrapper>
            ) : null}
            <BulkEditButtonWrapper>
              <Button
                key="bulk-edit"
                label="Bulk edit"
                variant="secondary"
                onClick={() => setBulkEditModalOpen()}
                disabled={!canEditProducts || hasArchivedFilter}
              />
            </BulkEditButtonWrapper>
            <AutoPublishButtonWrapper>
              <Button
                key="auto-publish"
                label="Manage auto-publish"
                variant="secondary"
                onClick={() => setAutoPublishModalOpen()}
              />
            </AutoPublishButtonWrapper>
          </Flex>
        </Flex>
        {table()}
      </Box>

      {bulkEditModalOpen ? (
        <BulkEditModal
          selectedProducts={selectedProducts}
          open={bulkEditModalOpen}
          headerButton={
            <HeaderButton
              text={buttonText}
              selectedProducts={selectedProducts}
              onClick={onHeaderButtonClick}
              productTableType={selected}
              publishDisabled={hasInvalidSelected}
            />
          }
          showInvalidError={hasInvalidSelected}
          reserveInvalidErrorSpace={
            bulkEditInvalidProducts && selected === ProductTableType.unpublished
          }
          productTable={table(true)}
          onClose={() => {
            setBulkEditModalClose();
            setSelectedProducts({});
          }}
        />
      ) : null}

      {autoPublishModalOpen ? (
        <AutoPublishModal
          open={autoPublishModalOpen}
          onClose={() => setAutoPublishModalClose()}
          productTypes={autoPublishSettings}
          storeName={storePayload?.store?.name}
        />
      ) : null}

      {revertModalOpen ? (
        <RevertModal
          open={revertModalOpen}
          onClose={setRevertModalClose}
          selectedProducts={selectedProducts}
          onBulkEditModalClose={setBulkEditModalClose}
          onSelectedProductsReset={setSelectedProducts}
        />
      ) : null}
      {clearModalOpen ? (
        <ClearModal
          open={clearModalOpen}
          onClose={setClearModalClose}
          onBulkEditModalClose={setBulkEditModalClose}
        />
      ) : null}
    </>
  );
};
